import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { assetTypeOf } from '@eis/core';
import { ManifestBlockField } from '../../tarifs/ui/PublishWizard';
import { ChildDeckungLine } from '../../tarif-authoring/ui/deckung/overview/ChildDeckungLine';
import { usePublishedTarifVersions, useGroupCoreConfigs } from '../api/group-tarifs';
import { joinGroupRefs, orderCandidates, splitGroupRefs } from '../model/pin-picker';

/**
 * The group manifest's `refs` block: four pickers and a text field.
 *
 * # Why `childTarifVersion` is a picker and not an input
 *
 * The pin is an EXACT physical version — `tarifservice:fahrrad/2026/1.1`, never a generation to be
 * resolved later (decision S-3). The service enforces three things about it: it must exist, it
 * must be PUBLISHED, and it must belong to the linked tarif; each of those is a 409 with a
 * machine-readable reason (service summary §5.1). Every one of them is knowable before the
 * request is sent, so the picker offers exactly the set that would be accepted. An operator can
 * still author a refused manifest — by pinning something that gets unpublished under them — but
 * they can no longer author one that was refused from the moment they typed it.
 *
 * The digest is shown beside each candidate, muted. Two versions of a tarif are distinguishable
 * only by their content, and the digest is what a four-eyes reviewer matches against the approval.
 */
export function GroupRefsField({
  linkedTarifSlug,
  groupTarifSlug,
  value,
  onChange,
  error,
}: {
  /** The slug of the tarif this group tarif references — the only source of child candidates. */
  readonly linkedTarifSlug: string;
  readonly groupTarifSlug: string;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly error: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const children = usePublishedTarifVersions(linkedTarifSlug);
  const cores = useGroupCoreConfigs(groupTarifSlug);

  const refs = splitGroupRefs(value);
  const candidates = orderCandidates(children.versions);
  const publishedAssets = (cores.data?.coreConfigs ?? []).filter(
    (asset) => asset.status === 'ASSET_STATUS_PUBLISHED',
  );
  const coreAssets = publishedAssets.filter(
    (asset) => assetTypeOf(asset.name ?? '') === 'group_core',
  );
  // The group's billing OVERRIDE (SDD-015 §5.5, B-7): optional, and "none" means the child tarif's
  // rules apply unchanged — which is what the empty option says.
  const billingAssets = publishedAssets.filter(
    (asset) => assetTypeOf(asset.name ?? '') === 'billing_config',
  );
  // The group's coverage OVERRIDE (SDD-018 §6, SDD-022 F-15): optional like billing; "none" means
  // the child's Tarif & Deckung applies whole.
  const deckungAssets = publishedAssets.filter(
    (asset) => assetTypeOf(asset.name ?? '') === 'tarif_deckung',
  );

  const update = (patch: Partial<typeof refs>): void => {
    onChange(joinGroupRefs({ ...refs, ...patch }));
  };

  return (
    <Stack spacing={2}>
      <TextField
        select
        required
        label={t('groupTarifs.publish.childVersion')}
        value={
          candidates.some((candidate) => candidate.ern === refs.childTarifVersion)
            ? refs.childTarifVersion
            : ''
        }
        onChange={(event) => {
          update({ childTarifVersion: event.target.value });
        }}
        fullWidth
        disabled={children.isPending}
        error={error !== ''}
        helperText={error === '' ? t('groupTarifs.publish.childVersionHint') : error}
        slotProps={{ select: { displayEmpty: false } }}
      >
        {candidates.map((candidate) => (
          <MenuItem key={candidate.ern} value={candidate.ern}>
            <Stack>
              <Box component="span" sx={{ fontFamily: 'monospace' }}>
                {candidate.ern}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {candidate.digest}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </TextField>
      {/* The child's "Tarif & Deckung" pin and its variants — what the group may allow (SDD-018 §5). */}
      <ChildDeckungLine
        tarifVersionErn={
          candidates.some((candidate) => candidate.ern === refs.childTarifVersion)
            ? refs.childTarifVersion
            : ''
        }
      />

      <TextField
        select
        required
        label={t('groupTarifs.publish.groupCore')}
        value={coreAssets.some((asset) => asset.ern === refs.groupCore) ? refs.groupCore : ''}
        onChange={(event) => {
          update({ groupCore: event.target.value });
        }}
        fullWidth
        disabled={cores.isPending}
        helperText={t('groupTarifs.publish.groupCoreHint')}
      >
        {coreAssets.map((asset) => (
          <MenuItem key={asset.ern} value={asset.ern}>
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {asset.ern}
            </Box>
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label={t('groupTarifs.publish.billingConfig')}
        value={
          billingAssets.some((asset) => asset.ern === refs.billingConfig) ? refs.billingConfig : ''
        }
        onChange={(event) => {
          update({ billingConfig: event.target.value });
        }}
        fullWidth
        disabled={cores.isPending}
        helperText={t('groupTarifs.publish.billingConfigHint')}
      >
        <MenuItem value="">{t('groupTarifs.publish.noBillingOverride')}</MenuItem>
        {billingAssets.map((asset) => (
          <MenuItem key={asset.ern} value={asset.ern}>
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {asset.ern}
            </Box>
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label={t('groupTarifs.publish.tarifDeckung')}
        value={
          deckungAssets.some((asset) => asset.ern === refs.tarifDeckung) ? refs.tarifDeckung : ''
        }
        onChange={(event) => {
          update({ tarifDeckung: event.target.value });
        }}
        fullWidth
        disabled={cores.isPending}
        helperText={t('groupTarifs.publish.tarifDeckungHint')}
      >
        <MenuItem value="">{t('groupTarifs.publish.noDeckungOverride')}</MenuItem>
        {deckungAssets.map((asset) => (
          <MenuItem key={asset.ern} value={asset.ern}>
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {asset.ern}
            </Box>
          </MenuItem>
        ))}
      </TextField>

      {/* The schema allows further refs beside the pins; nothing here can offer candidates for
          refs another service owns, so they stay free text. */}
      <ManifestBlockField
        block="refs"
        value={refs.other}
        onChange={(next) => {
          update({ other: next });
        }}
        error=""
      />
    </Stack>
  );
}
